/**
 * Minor-unit conversion for admin money inputs (KAN-284 W1).
 *
 * The API now carries configuration amounts as integers in a currency's MINOR
 * unit plus the `currency_code` they are denominated in — the column name no
 * longer states the currency. Admin forms still ask for and show MAJOR units
 * (an admin types "79", not "7900"), so every editor converts at its edges.
 *
 * The exponent table mirrors `apps/api/app/services/currency_minor.py`; keeping
 * the two in sync matters, because a wrong exponent silently reprices by 100×.
 */

const ZERO_DECIMAL = new Set([
  "BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA", "PYG",
  "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF",
]);
const THREE_DECIMAL = new Set(["BHD", "JOD", "KWD", "OMR", "TND"]);

/** Power of ten between a currency's major and minor unit. Unknown / absent
 * code falls back to 2 — the exponent every currency the platform supports has. */
export function minorUnitExponent(code?: string | null): number {
  const upper = (code ?? "").toUpperCase();
  if (ZERO_DECIMAL.has(upper)) return 0;
  if (THREE_DECIMAL.has(upper)) return 3;
  return 2;
}

/** Minor-unit integer → major-unit number for display / form values. */
export function minorToMajor(minor: number | null | undefined, code?: string | null): number {
  if (minor === null || minor === undefined) return 0;
  return minor / 10 ** minorUnitExponent(code);
}

/** Major-unit form input → minor-unit integer for the API. Rounds half-up once. */
export function majorToMinor(major: number, code?: string | null): number {
  return Math.round(major * 10 ** minorUnitExponent(code));
}

/**
 * Resolve one money slot that the API may send in either spelling (KAN-284 b2).
 *
 * The breakdown responses carry both `x_minor` and the legacy whole-unit
 * `x_czk` during the transition. The minor half wins whenever it is present:
 * it is the finer unit, so the koruna half can only ever be a rounded shadow
 * of it. Returns MAJOR units, which is what every renderer formats.
 */
export function moneyFromPair(
  minor: number | null | undefined,
  legacyMajor: number | null | undefined,
  code?: string | null,
): number {
  if (minor !== null && minor !== undefined) return minorToMajor(minor, code);
  return legacyMajor ?? 0;
}

/**
 * {@link moneyFromPair}'s sibling for callers that keep COMPUTING in minor.
 *
 * `moneyFromPair` hands back major units because that is what a renderer
 * formats. A caller that has arithmetic left to do — a percentage, a
 * multiplier, a sum of several slots — wants the integer instead: every step
 * done in major units rounds again, and the roundings accumulate.
 */
export function minorFromPair(
  minor: number | null | undefined,
  legacyMajor: number | null | undefined,
  code?: string | null,
): number {
  if (minor !== null && minor !== undefined) return minor;
  return majorToMinor(legacyMajor ?? 0, code);
}

/** {@link moneyFromPair} for a whole component map (`effective_breakdown_*`). */
export function moneyMapFromPair<T>(
  minor: Record<string, number> | null | undefined,
  legacyMajor: Record<string, T> | null | undefined,
  code?: string | null,
): Record<string, T | number> {
  if (minor && Object.keys(minor).length > 0) {
    return Object.fromEntries(
      Object.entries(minor).map(([k, v]) => [k, minorToMajor(v, code)]),
    );
  }
  return legacyMajor ?? {};
}
